import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, DollarSign, ArrowDownToLine } from 'lucide-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageHeaderSkeleton, AccountCardSkeleton } from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useFetchAccounts } from '@accountHooks';
import { REITHoldingSummary } from '@/components/reit-holding-summary/REITHoldingSummary';
import { REITMetricsCards } from '@/components/reit-metrics-cards/REITMetricsCards';
import { TimelineToggle, type TimePeriod } from '@/components/timeline-toggle/TimelineToggle';
import { REITValueTrendChart } from '@/components/reit-value-trend-chart/REITValueTrendChart';
import { REITDividendHistoryChart } from '@/components/reit-dividend-history-chart/REITDividendHistoryChart';
import { REITDividendHistoryTable } from '@/components/reit-dividend-history-table/REITDividendHistoryTable';
import REITForm from '@/components/reit-form/REITForm';
import REITDividendForm from '@/components/reit-dividend-form/REITDividendForm';
import REITDepositForm from '@/components/reit-deposit-form/REITDepositForm';
import {
  useUpdateReitHolding,
  useDeleteReitHolding,
  useFetchReitDividendPayments,
  useDeleteReitDividendPayment,
  useCreateReitDeposit,
  useRecordReitDividend,
  useFetchReitHoldings,
  useFetchReitTypes,
  useFetchReitSectors
} from "@/hooks/reits";
import {RecordREITDividendDto, CreateReitDepositDto} from "@/@types/reit.dto";

/**
 * REITDetailPage displays comprehensive information for a single REIT holding.
 * Accessible at /investments/reits/:holdingId.
 */
export default function REITDetailPage() {
  const { holdingId } = useParams<{ holdingId: string }>();
  const navigate = useNavigate();

  const [period, setPeriod] = useState<TimePeriod>('1Y');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [dividendOpen, setDividendOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);

  const { data: holdings = [], isLoading: holdingsLoading } = useFetchReitHoldings();
  const { data: payments = [], isLoading: paymentsLoading } = useFetchReitDividendPayments(holdingId);
  const { data: reitTypes = [] } = useFetchReitTypes();
  const { data: reitSectors = [] } = useFetchReitSectors();
  const { data: accounts = [] } = useFetchAccounts();

  const updateHolding = useUpdateReitHolding();
  const deleteHolding = useDeleteReitHolding();
  const createDeposit = useCreateReitDeposit();
  const recordDividend = useRecordReitDividend();
  const deleteDividend = useDeleteReitDividendPayment();

  const holding = useMemo(
    () => holdings.find((h) => h.id === holdingId) ?? null,
    [holdings, holdingId]
  );

  const typeLabel = useMemo(() => {
    if (!holding) return '';
    return reitTypes.find((t) => t.name === holding.reit_type)?.name ?? holding.reit_type;
  }, [holding, reitTypes]);

  const sectorLabel = useMemo(() => {
    if (!holding) return '';
    return reitSectors.find((s) => s.name === holding.sector)?.name ?? holding.sector;
  }, [holding, reitSectors]);

  const totalDividendsReceived = useMemo(
    () => payments.reduce((sum, p) => sum + p.total_amount, 0),
    [payments]
  );

  const isLoading = holdingsLoading || paymentsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-live="polite">
        <PageHeaderSkeleton />
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => <AccountCardSkeleton key={i} />)}
        </div>
        <AccountCardSkeleton />
        <AccountCardSkeleton />
      </div>
    );
  }

  if (!holding) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
        <p className="text-lg text-muted-foreground">REIT holding not found.</p>
        <Button asChild variant="outline">
          <Link to="/investments/reits">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Back to REITs
          </Link>
        </Button>
      </div>
    );
  }

  const handleSaveEdit = async (data) => {
    try {
      await updateHolding.mutateAsync({ id: holding.id, ...data });
      toast.success('REIT holding updated');
      setEditOpen(false);
    } catch {
      toast.error('Failed to update REIT holding');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteHolding.mutateAsync(holding.id);
      toast.success('REIT holding deleted');
      navigate('/investments/reits');
    } catch {
      toast.error('Failed to delete REIT holding');
      setDeleteOpen(false);
    }
  };

  const handleSaveDividend = async (data: RecordREITDividendDto) => {
    try {
      await recordDividend.mutateAsync(data);
      toast.success('Dividend payment recorded');
      setDividendOpen(false);
    } catch {
      toast.error('Failed to record dividend payment');
    }
  };

  const handleSaveDeposit = async (data: CreateReitDepositDto) => {
    try {
      await createDeposit.mutateAsync(data);
      toast.success('REIT deposit recorded');
      setDepositOpen(false);
    } catch {
      toast.error('Failed to record REIT deposit');
    }
  };

  const handleDeleteDividend = async (id: string) => {
    try {
      await deleteDividend.mutateAsync(id);
      toast.success('Dividend payment deleted');
    } catch {
      toast.error('Failed to delete dividend payment');
    }
  };

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/investments/reits')}
              aria-label="Back to REITs"
              className="min-h-[44px] min-w-[44px]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div>
              <p className="text-xs text-muted-foreground">REITs</p>
              <h1 className="text-xl font-bold">{holding.reit_name}</h1>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap" role="group" aria-label="Holding actions">
            <Button
              variant="outline"
              onClick={() => setDividendOpen(true)}
              className="min-h-[44px]"
            >
              <DollarSign className="mr-2 h-4 w-4" aria-hidden="true" />
              Record Dividend
            </Button>
            <Button
              variant="outline"
              onClick={() => setDepositOpen(true)}
              className="min-h-[44px]"
            >
              <ArrowDownToLine className="mr-2 h-4 w-4" aria-hidden="true" />
              Deposit
            </Button>
            <Button
              variant="outline"
              onClick={() => setEditOpen(true)}
              className="min-h-[44px]"
            >
              <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(true)}
              className="min-h-[44px] text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </div>
        </div>

        {/* Holding Summary */}
        <Card>
          <CardContent className="p-6">
            {/* TODO: pass in type and sector descriptions */}
            <REITHoldingSummary
              holding={holding}
              typeLabel={typeLabel}
              sectorLabel={sectorLabel}
            />
          </CardContent>
        </Card>

        {/* Metrics */}
        <REITMetricsCards holding={holding} totalDividendsReceived={totalDividendsReceived} />

        {/* Timeline Toggle */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Period:</span>
          <TimelineToggle selected={period} onChange={setPeriod} />
        </div>

        {/* Charts */}
        <REITValueTrendChart holding={holding} payments={payments} period={period} />
        <REITDividendHistoryChart
          payments={payments}
          period={period}
          currency={holding.currency}
        />

        {/* Dividend History Table */}
        <Card>
          <CardContent className="p-0">
            <REITDividendHistoryTable payments={payments} onDelete={handleDeleteDividend} />
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={(open) => { setEditOpen(open); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Edit REIT Holding</DialogTitle>
              <DialogDescription>Update the details of your REIT holding.</DialogDescription>
            </DialogHeader>
            <REITForm
              holding={holding}
              onSave={handleSaveEdit}
              onCancel={() => setEditOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Record Dividend Dialog */}
        <Dialog open={dividendOpen} onOpenChange={setDividendOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Record Dividend Payment</DialogTitle>
              <DialogDescription>Log a dividend payment for this holding.</DialogDescription>
            </DialogHeader>
            <REITDividendForm
              holdings={holdings}
              accounts={accounts}
              preselectedHoldingId={holding.id}
              onSave={handleSaveDividend}
              onCancel={() => setDividendOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Deposit Dialog */}
        <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Record REIT Deposit</DialogTitle>
              <DialogDescription>Purchase additional units in a REIT holding.</DialogDescription>
            </DialogHeader>
            <REITDepositForm
              holdings={holdings}
              accounts={accounts}
              preselectedHoldingId={holding.id}
              onSave={handleSaveDeposit}
              onCancel={() => setDepositOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent className="w-[95vw] sm:w-full sm:max-w-[500px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete REIT holding?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this holding and all associated dividend payment
                history. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel className="min-h-[44px] w-full sm:w-auto">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
